import React from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { BEIGE_600, GRAY_900, WHITE } from 'constants/styling/theme'
import { formatPrice } from 'utils/price/priceFormatter'

import { StruckPrice } from '../StruckPrice.component'
import type { CartExtraRow } from './useCartDisplay'

interface Props {
  extra: CartExtraRow
  currency: string | null
}

/**
 * An order-level charge the client did not pick as a product, such as a travel fee, shown as a cart line of its
 * own so the total reconciles with what is listed (QA-654). Neither edited nor removed here: it follows the choice
 * that produced it on the Instructions step.
 */
export const CartExtraCard: React.FC<Props> = ({ extra, currency }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      gap={0.8}
      data-cy="pf-revamp-cart-extra-line"
      sx={{ backgroundColor: WHITE, border: `1px solid ${BEIGE_600}`, borderRadius: '0.8rem', padding: 1.2 }}
    >

      <Typography variant="text-sm" fontWeight={550} color={GRAY_900} flex={1} minWidth={0}>
        {/* `defaultValue`: a kind whose copy is not in the locale files yet shows its kind, never the raw key. */}
        {t(extra.labelKey, { defaultValue: extra.kind, count: extra.count })}
      </Typography>

      <Typography variant="text-sm" fontWeight={550} color={GRAY_900} sx={{ whiteSpace: 'nowrap' }}>
        {currency
          ? <StruckPrice
            price={formatPrice(extra.amount, currency)}
            base={extra.baseAmount ? formatPrice(extra.baseAmount, currency) : null}
            variant="text-sm"
          />
          : t('purchase_flow_revamp:cart.price_unavailable')}
      </Typography>

    </Stack>
  )
}
